import { Component, inject, OnInit } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { WebSocketService } from '../../services/websocket.service';
import { MagasinSimulationService } from '../../services/magasin-simulation.service';
import { MagasinService } from '../../services/magasin.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss'
})
export class AdminLayoutComponent implements OnInit {
  magasinSimulation = inject(MagasinSimulationService);
  private magasinService = inject(MagasinService);

  magasins: any[] = [];
  showMagasinSelector = false;
  selectedMagasinId: number | null = null;

  get isSuperAdmin(): boolean {
    return this.authService.currentUser()?.profils?.some(p => p.code === 'super_admin') ?? false;
  }

  get menuItems() {
    const isSuperAdmin = this.isSuperAdmin;

    return [
      {
        label: 'Tableau de bord',
        icon: 'pi pi-home',
        route: '/dashboard'
      },
      {
        label: 'Produits',
        icon: 'pi pi-box',
        route: '/produits'
      },
      {
        label: 'Commandes',
        icon: 'pi pi-shopping-cart',
        route: '/commandes'
      },
      {
        label: 'Utilisateurs',
        icon: 'pi pi-users',
        route: '/utilisateurs'
      },
      ...(isSuperAdmin ? [{
        label: 'Logs Modifications',
        icon: 'pi pi-history',
        route: '/logs-modifications'
      }] : []),
      ...('qa' in environment && environment.qa ? [{
        label: 'Test Notifications',
        icon: 'pi pi-bell',
        route: '/test-notifications'
      }] : [])
    ];
  }

  guideUrl = '/public/tutoriel-magasins/guide-utilisation-magasins-labase.pdf';

  private websocketService = inject(WebSocketService);

  constructor(
    public authService: AuthService,
    private router: Router,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    if (this.isSuperAdmin) {
      this.showMagasinSelector = true;
      this.selectedMagasinId = this.magasinSimulation.simulatedMagasinId();
      this.magasinService.getMagasinsActifsAvecQA().subscribe(magasins => {
        this.magasins = magasins.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
      });
    }
  }

  selectMagasin(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const magasinId = parseInt(select.value, 10);
    if (!magasinId) {
      this.clearSimulation();
      return;
    }
    const magasin = this.magasins.find(m => m.id === magasinId);
    if (magasin) {
      this.magasinSimulation.setSimulation(magasinId, magasin.nom);
      this.selectedMagasinId = magasinId;
      window.location.reload();
    }
  }

  clearSimulation(): void {
    this.magasinSimulation.clearSimulation();
    this.selectedMagasinId = null;
    window.location.reload();
  }

  async logout(): Promise<void> {
    const confirmed = await this.confirmationService.confirm({
      title: 'Déconnexion',
      message: 'Êtes-vous sûr de vouloir vous déconnecter ?',
      confirmText: 'Déconnexion',
      cancelText: 'Annuler'
    });

    if (confirmed) {
      // Disconnect WebSocket before logout
      try {
        this.websocketService.disconnect();
        console.log('[Admin Logout] WebSocket disconnected');
      } catch (wsError) {
        console.error('[Admin Logout] Error disconnecting WebSocket:', wsError);
      }

      this.authService.logout();
    }
  }

  isActiveRoute(route: string): boolean {
    return this.router.url === route;
  }

  getProfilsLabel(): string {
    const user = this.authService.currentUser();
    if (!user?.profils || user.profils.length === 0) {
      return 'Aucun profil';
    }
    return user.profils.map(p => p.nom).join(', ');
  }

  openGuide(): void {
    const guideFullUrl = `${environment.apiUrl}${this.guideUrl}`;
    window.open(guideFullUrl, '_blank');
  }
}
